import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Text, TextInput, Group, Stack, Modal, Divider } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useTrans } from "core";
import api from "../../services/ApiService";
import { MSIcon } from "components/MSIcon.jsx";
import { Api_Apartment, Api_Reserve } from "../../../../constants/SubApi.js";

export default function ApartmentReserve() {
	const { id } = useParams();
	const { t } = useTrans();
	const navigate = useNavigate();
	const [fromDate, setFromDate] = React.useState(new Date());
	const [toDate, setToDate] = React.useState(new Date());
	const [numOfDays, setNumOfDays] = React.useState(0);
	const [total, setTotal] = React.useState(0);
	const [dailyPrice, setDailyPrice] = React.useState(0);
	const [loading, setLoading] = React.useState(false);
	const [successOpened, setSuccessOpened] = React.useState(false);
	const [successData, setSuccessData] = React.useState(null);

	const onSuccess = () => {
		setSuccessOpened(false);
		navigate("/");
	};

	React.useEffect(() => {
		loadApartment();
	}, [id]);

	const loadApartment = async () => {
		try {
			const res = await api.get(`/${Api_Apartment}/${id}`);
			setDailyPrice(Number(res.dailyPrice) || 0);
			calculateTotals(Number(res.dailyPrice) || 0);
		} catch (err) {
			console.error("Failed to load apartment", err);
		}
	};

	const showApiError = (err) => {
		let message;
		const messageKey = err?.response?.data?.message || err?.data?.message || "messages.error";
		if (err.data.success === false && err.data.params.confirmEnd) {
			const confirmEndDate = new Date(err.data.params.confirmEnd);
			const options = { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" };
			const formattedDate = confirmEndDate.toLocaleDateString("ar-SY", options);
			message = t(messageKey);
			message = message.replace("{{confirmDate}}", formattedDate);
		} else {
			message = t(messageKey);
		}

		notifications.show({
			color: "red",
			title: t("messages.error"),
			message: message,
			icon: <MSIcon name="alert-circle" />,
		});
	};

	React.useEffect(() => {
		calculateTotals();
	}, [fromDate, toDate]);

	const calculateTotals = () => {
		const from = new Date(fromDate);
		const to = new Date(toDate);

		const diff = Math.max(1, Math.ceil((to - from) / (1000 * 60 * 60 * 24)));

		setNumOfDays(diff);
		setTotal(diff * dailyPrice);
	};

	const submitReserve = async () => {
		setLoading(true);

		try {
			const payload = {
				apartmentId: id,
				startDate: new Date(fromDate).getTime(),
				endDate: new Date(toDate).getTime(),
				totalPrice: total,
			};

			const response = await api.post(`/${Api_Reserve}`, payload);

			if (response.data?.success === false) {
				showApiError({ response });
				setLoading(false);
				return;
			}

			setSuccessData({
				endDate: new Date(response.data.confirmEnd),
				code: response.data.code,
			});

			setSuccessOpened(true);
		} catch (err) {
			showApiError(err);
		}

		setLoading(false);
	};

	return (
		<>
			{/* SUCCESS DIALOG */}
			<Modal opened={successOpened} onClose={() => setSuccessOpened(false)} centered withCloseButton={false}>
				<Card
					radius="md"
					p="lg"
					style={{
						textAlign: "center",
					}}
				>
					<MSIcon name="CheckCircleOutline" />
					<Text mt="md" fw={700} size="xl">
						{t("bookApartment.completBook")}
					</Text>

					{successData && (
						<>
							<Text mt="sm">
								{t("bookApartment.tempoApartment")}: {successData.endDate.toLocaleDateString()}
							</Text>

							<Text mt="xs">
								{t("bookApartment.code")}: <b>{successData.code}</b>
							</Text>
						</>
					)}

					<Button mt="lg" radius="md" fullWidth onClick={() => onSuccess()}>
						{t("bookApartment.ok")}
					</Button>
				</Card>
			</Modal>

			{/* MAIN PAGE */}
			<Card shadow="md" radius="md" p="lg" withBorder>
				<Stack gap="lg">
					<Text fw={700} size="lg">
						{t("baseApartmentDetails.book")}
					</Text>

					{/* FROM DATE */}
					<DatePickerInput
						label={t("bookApartment.fromDate")}
						placeholder="Pick date"
						value={fromDate}
						onChange={(value) => {
							setFromDate(value);
							if (toDate < value) setToDate(value);
						}}
						minDate={new Date()}
					/>

					{/* TO DATE */}
					<DatePickerInput
						label={t("bookApartment.toDate")}
						placeholder="Pick date"
						value={toDate}
						onChange={(value) => {
							setToDate(value);
							if (value < fromDate) setFromDate(value);
						}}
						minDate={fromDate}
					/>

					<Divider />

					{/* NUMBER OF DAYS */}
					<TextInput label={t("bookApartment.numberDays")} value={numOfDays.toString()} readOnly />

					{/* DAILY PRICE */}
					<TextInput label={t("bookApartment.dailyPrice")} value={dailyPrice.toFixed(0)} readOnly />

					{/* TOTAL */}
					<TextInput label={t("bookApartment.total")} value={total.toFixed(0)} readOnly />

					<Group grow mt="lg">
						<Button leftSection={<MSIcon name="Check" />} onClick={submitReserve} loading={loading} color="blue">
							{t("bookApartment.completBook")}
						</Button>

						<Button leftSection={<MSIcon name="Close" />} variant="outline" onClick={() => navigate("/")} color="red">
							{t("region.cancel")}
						</Button>
					</Group>
				</Stack>
			</Card>
		</>
	);
}
